import { FATHOM_TO_METER, FOOT_TO_METER } from '@racemap/utilities/consts/units';
import {
  distanceUnitByUnitType,
  elevationUnitByUnitType,
} from '@racemap/utilities/functions/units';
import { UnitType } from '@racemap/utilities/types/units';
import { useGesture } from '@use-gesture/react';
import { clamp } from 'lodash';
import { type SyntheticEvent, memo, useMemo, useRef } from 'react';
import { type RenderInfoBuffer, getRenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_STYLE,
  SHADOW_TRACK_STEP,
  STATISTIC_FILTER_SIZE,
} from 'src/lib/player/constants';
import { memoizedAscent, memoizedDescent } from 'src/lib/player/point_utils';
import { getHiddenIds, getSelectedIds } from 'src/lib/player/selectors';
import { getStarterInfos } from 'src/lib/player/statistics';
import { type PoiType, type StarterType, type XY, useStore } from 'src/lib/player/store';
import {
  NBSP,
  convertElevationUnitToMeter,
  convertMeterToElevationUnit,
  distanceUnitToMeterFactor,
  flatMap,
  formatDistance,
  formatElevation,
  meanFilter,
  meterToDistanceUnitFactor,
  onlyMouseEvents,
} from 'src/lib/player/utils';
import type TrackView from 'src/lib/player/utils/TrackView';
import { useBaseColor } from 'src/lib/player/utils/baseColor';
import { useMeasure } from 'src/lib/player/utils/hooks';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import tinycolor from 'tinycolor2';
import { PoiIconSVG } from '../SideBar/PoiListItem';
import SVGChart, { type Bounds } from './SVGChart';
import { findGridLinesDistance, findGridLinesElevation } from './gridlines';

const MAX_DOT_COUNT = 1000;
const DOT_RADIUS = 3;

const MIN_Y_RANGE_METER = 100;
const MIN_Y_RANGE_FOOT = 300;
const MIN_Y_RANGE_FATHOM = 50;

function minYRangeByUnitType(unitType: UnitType): number {
  switch (unitType) {
    case UnitType.IMPERIAL:
      return MIN_Y_RANGE_FOOT * FOOT_TO_METER;
    case UnitType.NAUTICAL:
      return MIN_Y_RANGE_FATHOM * FATHOM_TO_METER;
    case UnitType.METRIC:
      return MIN_Y_RANGE_METER;
    default:
      return MIN_Y_RANGE_METER;
  }
}

function* chartDataFromShadowTrack(shadowTrack: TrackView | null): Generator<XY, void, void> {
  if (shadowTrack == null) return;
  for (let i = 0; i < shadowTrack.size(); i++) {
    const elevation = shadowTrack.getElevation(i);
    const correctedElevation = Number.isNaN(elevation) ? 0 : elevation;
    yield { x: i * SHADOW_TRACK_STEP, y: correctedElevation };
  }
}

function getInterpolatedElevation(track: Array<XY>, x: number): number {
  const hiIdx = track.findIndex((xy) => xy.x >= x);
  if (hiIdx > 0) {
    const hi = track[hiIdx];
    const lo = track[hiIdx - 1];
    const fraction = (x - lo.x) / (hi.x - lo.x);
    return lo.y * (1 - fraction) + hi.y * fraction;
  }
  if (hiIdx === 0) {
    return track[0].y;
  }
  if (track[track.length - 1].x < x) {
    return track[track.length - 1].y;
  }
  return Number.NaN;
}

function getInterpolatedY(elevation: number, minY: number, maxY: number, bounds: Bounds): number {
  return maxY === minY
    ? bounds.paddingTop + 0.5 * bounds.innerHeight
    : bounds.paddingTop +
        (1 - (clamp(elevation, minY, maxY) - minY) / (maxY - minY)) * bounds.innerHeight;
}

function getInterpolatedStatistic(track: Array<number>, index: number): number {
  const loIdx = Math.floor(index / STATISTIC_FILTER_SIZE);
  const hiIdx = Math.ceil(index / STATISTIC_FILTER_SIZE);
  const fraction = (index / STATISTIC_FILTER_SIZE) % 1;
  if (loIdx >= 0 && loIdx < track.length) {
    if (hiIdx >= 0 && hiIdx < track.length) {
      return track[loIdx] * (1 - fraction) + track[hiIdx] * fraction;
    }
    return track[loIdx];
  }
  return Number.NaN;
}

type HeightChartProps = {
  height: number;
  width: number;
};

const HeightChartPolyline = memo(function HeightChartPolyline({
  filteredTrack,
  maxX,
  minY,
  maxY,
  bounds,
  lineColor,
}: {
  filteredTrack: Array<XY>;
  maxX: number;
  minY: number;
  maxY: number;
  bounds: Bounds;
  lineColor: string;
}): JSX.Element {
  return (
    <>
      <polygon
        points={[{ x: 0, y: minY }, ...filteredTrack, { x: maxX, y: minY }]
          .map(
            (point) =>
              `${bounds.paddingLeft + (point.x / maxX) * bounds.innerWidth} ${getInterpolatedY(
                point.y,
                minY,
                maxY,
                bounds,
              )}`,
          )
          .join(' ')}
        fill={`${tinycolor(lineColor).setAlpha(0.6).toRgbString()}`}
        stroke={lineColor}
        strokeWidth={1}
      />
    </>
  );
});

const HeightChartProgressPolyline = memo(function HeightChartProgressPolyline({
  filteredTrack,
  maxX,
  minY,
  maxY,
  bounds,
  progress,
  lineColor,
}: {
  filteredTrack: Array<XY>;
  maxX: number;
  minY: number;
  maxY: number;
  bounds: Bounds;
  progress: number;
  lineColor: string;
}): JSX.Element {
  const cutoffX = clamp(progress, 0, maxX);
  const trackOnlyUpToProgress = filteredTrack.filter((point) => point.x <= progress);
  const boundsWithCutoff = { ...bounds, innerWidth: (cutoffX / maxX) * bounds.innerWidth };

  return (
    <>
      <polygon
        points={[{ x: 0, y: minY }, ...trackOnlyUpToProgress, { x: cutoffX, y: minY }]
          .map(
            (point) =>
              `${boundsWithCutoff.paddingLeft + (point.x / cutoffX) * boundsWithCutoff.innerWidth} ${getInterpolatedY(
                point.y,
                minY,
                maxY,
                boundsWithCutoff,
              )}`,
          )
          .join(' ')}
        fill={tinycolor(lineColor).setAlpha(0.6).toRgbString()}
        stroke={lineColor}
        strokeWidth={1}
      />
    </>
  );
});

function HeightChartStarter({
  starterOffset,
  starter,
  bounds,
  maxX,
  minY,
  maxY,
  filteredTrack,
  lineColor,
}: {
  starterOffset: number;
  starter: StarterType;
  bounds: Bounds;
  maxX: number;
  minY: number;
  maxY: number;
  filteredTrack: Array<XY>;
  lineColor: string;
}) {
  if (Number.isNaN(starterOffset)) return null;
  const starterLineX = bounds.paddingLeft + clamp(starterOffset / maxX, 0, 1) * bounds.innerWidth;
  const elevation = getInterpolatedElevation(filteredTrack, starterOffset);
  const lowerY = getInterpolatedY(elevation, minY, maxY, bounds);
  const upperY = Math.max(0, lowerY - 50);
  return (
    <>
      <text
        x={starterLineX + 4}
        y={upperY + 11}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'left',
          textAnchor: 'start',
          fill: lineColor,
        }}
      >
        {starter.startNumber}
      </text>
      <line
        x1={starterLineX}
        x2={starterLineX}
        y1={upperY + 1}
        y2={lowerY}
        stroke={starter.markerColor.hex}
        strokeWidth={2}
      />
    </>
  );
}

function HeightChartStarterDots({
  renderInfoBuffer,
  renderInfoOffset,
  renderInfoCount,
  bounds,
  maxX,
  minY,
  maxY,
  filteredTrack,
  lineColor,
}: {
  renderInfoBuffer: RenderInfoBuffer;
  renderInfoOffset: number;
  renderInfoCount: number;
  bounds: Bounds;
  maxX: number;
  minY: number;
  maxY: number;
  filteredTrack: Array<XY>;
  lineColor: string;
}) {
  const output = [];
  for (
    let i = clamp(renderInfoOffset, 0, renderInfoBuffer.length);
    i <
    clamp(renderInfoOffset + Math.min(MAX_DOT_COUNT, renderInfoCount), 0, renderInfoBuffer.length);
    i++
  ) {
    const offset = renderInfoBuffer.getOffset(i);
    const opacity = renderInfoBuffer.getOpacity(i);
    if (renderInfoBuffer.getIsVisible(i) && offset >= 0 && !Number.isNaN(offset) && opacity > 0) {
      const starterDotX = bounds.paddingLeft + clamp(offset / maxX, 0, 1) * bounds.innerWidth;
      const elevation = getInterpolatedElevation(filteredTrack, offset);
      const starterDotY = getInterpolatedY(elevation, minY, maxY, bounds);
      output.push(
        <circle
          key={i}
          cx={starterDotX}
          cy={starterDotY}
          r={DOT_RADIUS}
          fill={`rgb(${renderInfoBuffer.colors[i * 4]}, ${renderInfoBuffer.colors[i * 4 + 1]},${
            renderInfoBuffer.colors[i * 4 + 2]
          })`}
          stroke={lineColor}
          opacity={opacity}
        />,
      );
    }
  }
  return <>{output}</>;
}

function hitTestDot(renderInfoBuffer: RenderInfoBuffer, bounds: Bounds, maxX: number, x: number) {
  if (renderInfoBuffer.length > MAX_DOT_COUNT) return null;
  for (let i = 0; i < renderInfoBuffer.length; i++) {
    const isVisible = renderInfoBuffer.getIsVisible(i);
    const offset = renderInfoBuffer.getOffset(i);
    const opacity = renderInfoBuffer.getOpacity(i);
    if (isVisible && offset >= 0 && !Number.isNaN(offset) && opacity > 0) {
      const starterDotX = bounds.paddingLeft + clamp(offset / maxX, 0, 1) * bounds.innerWidth;
      if (x >= starterDotX - DOT_RADIUS && x <= starterDotX + DOT_RADIUS) {
        return renderInfoBuffer.starters[i];
      }
    }
  }
  return null;
}

function HeightChartPoi({
  poi,
  bounds,
  maxX,
  minY,
  maxY,
  filteredTrack,
  isSelected,
  onClick,
  onHover,
  onHoverOut,
}: {
  poi: PoiType;
  bounds: Bounds;
  maxX: number;
  minY: number;
  maxY: number;
  filteredTrack: Array<XY>;
  isSelected: boolean;
  onClick: (event: SyntheticEvent) => void;
  onHover: (event: SyntheticEvent) => void;
  onHoverOut: (event: SyntheticEvent) => void;
}) {
  if (poi.offset == null || Number.isNaN(poi.offset)) return null;
  const poiX = bounds.paddingLeft + clamp(poi.offset / maxX, 0, 1) * bounds.innerWidth;
  const elevation = getInterpolatedElevation(filteredTrack, poi.offset);
  const lowerY = getInterpolatedY(elevation, minY, maxY, bounds);

  return (
    <>
      {isSelected && (
        <line x1={poiX} x2={poiX} y1={2} y2={lowerY} stroke={poi.markerColor.hex} strokeWidth={2} />
      )}
      <g
        transform={`translate(${poiX - 10} 0) scale(0.5 0.5)`}
        {...onClickGenerator(() => onClick)}
        onPointerEnter={onlyMouseEvents(onHover)}
        onPointerLeave={onlyMouseEvents(onHoverOut)}
      >
        <PoiIconSVG poi={poi} />
      </g>
    </>
  );
}

function HeightChartCrosshair({
  bounds,
  unitType,
  highlightedOffset,
  track,
  filteredTrack,
  minY,
  maxY,
  lineColor,
}: {
  bounds: Bounds;
  unitType: UnitType;
  highlightedOffset: number;
  track: TrackView;
  filteredTrack: Array<XY>;
  minY: number;
  maxY: number;
  lineColor: string;
}) {
  const trackOffset = Math.floor(highlightedOffset / SHADOW_TRACK_STEP);
  const trackOffsetRelative = trackOffset / track.size();
  const hoverX = trackOffsetRelative * bounds.innerWidth + bounds.paddingLeft;

  const maxDistance = track.size() * SHADOW_TRACK_STEP;
  const distance = trackOffsetRelative * maxDistance;
  const elevation = getInterpolatedElevation(filteredTrack, highlightedOffset);

  const elevationLabel = formatElevation(elevation, unitType);
  const distanceLabel = formatDistance(distance, unitType);

  const accumulatedAscent = getInterpolatedStatistic(memoizedAscent(track), trackOffset);
  const accumulatedDescent = getInterpolatedStatistic(memoizedDescent(track), trackOffset);
  const ascentLabel = formatElevation(accumulatedAscent, unitType);
  const descentLabel = formatElevation(accumulatedDescent, unitType);

  const ascentLabelWidth =
    Math.max(ascentLabel.split(NBSP)[0].length, descentLabel.split(NBSP)[0].length) * 7 + 35;

  const posY = getInterpolatedY(elevation, minY, maxY, bounds);

  if (distance < 0 || distance > maxDistance) return null;

  return (
    <g pointerEvents="none">
      <g transform={`translate(${hoverX - ascentLabelWidth} 1)`}>
        <path
          fill={tinycolor(lineColor).setAlpha(0.4).toRgbString()}
          d="M12,0.5C12,0.8,11.8,1,11.5,1H0.5C0.2,1,0,0.8,0,0.5S0.2,0,0.5,0h11.1C11.8,0,12,0.2,12,0.5z"
        />
        <polygon
          fill={lineColor}
          points="2.9,3.3 3.5,3.8 5.5,1.6 5.5,12 6.5,12 6.5,1.6 8.5,3.8 9.1,3.3 6,0 	"
        />
      </g>
      <text
        x={hoverX - 4}
        y={11}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'end',
          fill: lineColor,
        }}
      >
        {ascentLabel}
      </text>
      <g transform={`translate(${hoverX - ascentLabelWidth} 16)`}>
        <path
          d="M12,0.5c0,0.3 -0.2,0.5 -0.5,0.5l-11,0c-0.3,0 -0.5,-0.2 -0.5,-0.5c0,-0.3 0.2,-0.5 0.5,-0.5l11.1,0c0.2,0 0.4,0.2 0.4,0.5Z"
          fill={tinycolor(lineColor).setAlpha(0.4).toRgbString()}
        />
        <path
          d="M2.9,8.7l0.6,-0.5l2,2.2l0,-10.4l1,0l0,10.4l2,-2.2l0.6,0.5l-3.1,3.3l-3.1,-3.3Z"
          fill={lineColor}
        />
      </g>
      <text
        x={hoverX - 4}
        y={0 + 11 + 15}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'end',
          fill: lineColor,
        }}
      >
        {descentLabel}
      </text>
      <text
        x={bounds.paddingLeft - 6}
        y={posY + 4}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'end',
          fill: lineColor,
        }}
      >
        {elevationLabel}
      </text>
      <text
        x={hoverX}
        y={bounds.paddingTop + bounds.innerHeight + bounds.paddingBottom - 3}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'middle',
          fill: lineColor,
        }}
      >
        {distanceLabel}
      </text>
      <line
        x1={hoverX}
        x2={hoverX}
        y1={0}
        y2={bounds.paddingTop + bounds.innerHeight}
        stroke={lineColor}
        strokeWidth={1}
      />
      <line
        x1={bounds.paddingLeft}
        x2={bounds.paddingLeft + bounds.innerWidth}
        y1={posY}
        y2={posY}
        stroke={lineColor}
        strokeWidth={1}
      />
    </g>
  );
}

export default function HeightChart({ width, height }: HeightChartProps): JSX.Element {
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const setHighlightedOffset = useStore((state) => state.actions.setHighlightedOffset);
  const elevationChartOptions = useStore(
    (state) => state.events[state.currentEventIndices[0]].options.elevationChartOptions,
  );
  const unitType = useStore((state) => state.options.unitType);
  const currentEvent1 = useStore((state) => state.events[state.currentEventIndices[0]]);
  const track = currentEvent1.chunkedShadowTrack;
  const renderInfoBuffer = useStore(getRenderInfoBuffer);
  const highlightedId = useStore((state) => state.timer.highlighted);
  const selectedIds = useStore((state) => getSelectedIds(state.timer));
  const hiddenIds = useStore((state) => getHiddenIds(state.timer));
  const highlightedOffset = useStore((state) => state.timer.highlightedOffset);
  const pois = useStore((state) => state.events[state.currentEventIndices[0]].pois);
  const elevationShowProgress = useStore((state) => state.options.elevationShowProgress);
  const elevationShowGrid = useStore((state) => state.options.elevationShowGrid);
  const elevationShowAxis = useStore((state) => state.options.elevationShowAxis);
  const elevationShowPOI = useStore((state) => state.options.elevationShowPOI);

  const starterInfos = useStore((state) =>
    flatMap(state.timer.selected, (selectedId) => getStarterInfos(state, selectedId)).filter(
      (starterInfo) =>
        !hiddenIds.has(starterInfo.id) &&
        starterInfo.starter.eventIndex === state.currentEventIndices[0],
    ),
  );
  const [backgroundColor] = useBaseColor();
  const lineColor = tinycolor(backgroundColor).isLight() ? 'black' : 'white';

  function isStarter(id: string | null) {
    return id != null && starterInfos.some((s) => s.id === id);
  }
  function isPoi(id: string | null) {
    return id != null && pois.some((p) => p.id === id);
  }

  const [chartRef, chartBounds] = useMeasure({ debounce: 100, scroll: false });
  const boundsRef = useRef<Bounds | null>(null);
  const bind = useGesture(
    {
      onMove: ({ xy: [x] }) => {
        if (boundsRef.current == null || track == null) return;

        const hoverX = x - chartBounds.left;
        const newHighlightedOffsetRelative =
          (hoverX - boundsRef.current.paddingLeft) / boundsRef.current.innerWidth;
        const newHighlightedOffset =
          newHighlightedOffsetRelative * (track.size() * SHADOW_TRACK_STEP);
        setHighlightedOffset(
          clamp(newHighlightedOffset, 0, (track.size() - 1) * SHADOW_TRACK_STEP),
        );
        if (!isPoi(highlightedId)) {
          const starter = hitTestDot(renderInfoBuffer, boundsRef.current, maxX, hoverX);
          if (starter != null) {
            setHighlighted(starter.id);
            return;
          }
          setHighlighted(null);
        }
      },
      onDrag: ({ tap, last, xy: [x] }) => {
        if (boundsRef.current == null || track == null) return;

        if (last) {
          setHighlightedOffset(-1);
        } else {
          const hoverX = x - chartBounds.left;
          const newHighlightedOffsetRelative =
            (hoverX - boundsRef.current.paddingLeft) / boundsRef.current.innerWidth;
          const newHighlightedOffset =
            newHighlightedOffsetRelative * (track.size() * SHADOW_TRACK_STEP);
          setHighlightedOffset(
            clamp(newHighlightedOffset, 0, (track.size() - 1) * SHADOW_TRACK_STEP),
          );
        }

        if (tap) {
          const starter = hitTestDot(
            renderInfoBuffer,
            boundsRef.current,
            maxX,
            x - chartBounds.left,
          );
          if (starter != null) {
            toggleSelectedStarter(starter.id);
          }
        }
      },
      onHover: ({ active }) => {
        if (!active) {
          setHighlightedOffset(-1);
          if (!isPoi(highlightedId)) {
            setHighlighted(null);
          }
        }
      },
    },
    { drag: { filterTaps: true } },
  );

  const highlightedStarterInfo = (() => {
    if (highlightedId == null || starterInfos.some((s) => s.starter.id === highlightedId))
      return null;
    const index = renderInfoBuffer.starters.findIndex((s) => s?.id === highlightedId);
    if (index < 0) return null;
    return {
      offset: renderInfoBuffer.getOffset(index),
      starter: renderInfoBuffer.starters[index],
    };
  })();
  const leadingStarterInfo = (() => {
    const selectedStarterInfos = starterInfos.filter((s) => selectedIds.has(s.id));
    if (selectedStarterInfos.length === 0) return null;
    return selectedStarterInfos.reduce((prev, curr) => (prev.offset > curr.offset ? prev : curr));
  })();

  const coarseWidth = Math.round(width / 100) * 100;
  const filteredTrack = useMemo(() => {
    const fullTrack = Array.from(chartDataFromShadowTrack(track));
    const requestedPointCount = Math.round(coarseWidth / 3);
    return Array.from(meanFilter(fullTrack, clamp(requestedPointCount, 200, 1000)));
  }, [track, coarseWidth]);

  const minX = 0;
  const minY = elevationChartOptions?.min ?? 0;
  let maxX = 0;
  let maxY = 0;
  for (const point of filteredTrack) {
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  maxY = Math.max(maxY, minYRangeByUnitType(unitType));

  if (elevationChartOptions?.max != null) {
    maxY = elevationChartOptions.max;
  }

  const xAxis = {
    min: minX,
    max: maxX,
    ticks: findGridLinesDistance(
      minX,
      maxX * meterToDistanceUnitFactor(unitType),
      clamp(Math.round(width / 100), 5, 15),
    ).map((value): [number, string] => [
      value * distanceUnitToMeterFactor(unitType),
      `${Math.round(value)} ${distanceUnitByUnitType(unitType)}`,
    ]),
  };
  const yAxis = {
    min: minY,
    max: maxY,
    ticks: findGridLinesElevation(
      minY * convertMeterToElevationUnit(unitType),
      maxY * convertMeterToElevationUnit(unitType),
      clamp(Math.round(height / 50), 3, 10),
    ).map((value): [number, string] => [
      value * convertElevationUnitToMeter(unitType),
      `${Math.round(value)} ${elevationUnitByUnitType(unitType)}`,
    ]),
  };

  return (
    <SVGChart
      width={width}
      height={height - 13}
      style={{ marginTop: 12 }}
      xAxis={xAxis}
      yAxis={yAxis}
      gestureBind={bind()}
      dimAxis={highlightedOffset !== -1}
      forwardRef={chartRef}
      boundsRef={boundsRef}
      lineColor={lineColor}
      showGrid={elevationShowGrid ?? undefined}
      showAxis={elevationShowAxis ?? undefined}
    >
      {(bounds) => {
        return (
          <>
            {highlightedOffset !== -1 && track != null && (
              <HeightChartCrosshair
                bounds={bounds}
                unitType={unitType}
                highlightedOffset={highlightedOffset}
                track={track}
                filteredTrack={filteredTrack}
                minY={minY}
                maxY={maxY}
                lineColor={lineColor}
              />
            )}
            {starterInfos.map((starterInfo) => {
              const renderIndex = renderInfoBuffer.starters.findIndex(
                (s) => s.id === starterInfo.starter.id,
              );
              const renderOffset = renderIndex >= 0 ? renderInfoBuffer.getOffset(renderIndex) : -1;
              const starterOffset =
                !Number.isNaN(renderOffset) && renderOffset > 0 ? renderOffset : starterInfo.offset;
              return (
                <HeightChartStarter
                  key={`starterline-${starterInfo.starter.id}`}
                  starterOffset={starterOffset}
                  starter={starterInfo.starter}
                  bounds={bounds}
                  maxX={maxX}
                  minY={minY}
                  maxY={maxY}
                  filteredTrack={filteredTrack}
                  lineColor={lineColor}
                />
              );
            })}
            {pois
              .filter((poi) => !hiddenIds.has(poi.id))
              .map(
                (poi) =>
                  (elevationShowPOI ?? true) &&
                  poi.offset != null && (
                    <HeightChartPoi
                      key={`poi-${poi.id}`}
                      poi={poi}
                      bounds={bounds}
                      maxX={maxX}
                      minY={minY}
                      maxY={maxY}
                      filteredTrack={filteredTrack}
                      isSelected={selectedIds.has(poi.id)}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleSelectedStarter(poi.id);
                      }}
                      onHover={(event) => {
                        if (!isStarter(highlightedId)) {
                          event.stopPropagation();
                          setHighlighted(poi.id);
                        }
                      }}
                      onHoverOut={(event) => {
                        if (!isStarter(highlightedId)) {
                          event.stopPropagation();
                          setHighlighted(null);
                        }
                      }}
                    />
                  ),
              )}
            {highlightedStarterInfo != null && (
              <HeightChartStarter
                key={`starterline-${highlightedStarterInfo.starter.id}`}
                starterOffset={highlightedStarterInfo.offset}
                starter={highlightedStarterInfo.starter}
                bounds={bounds}
                maxX={maxX}
                minY={minY}
                maxY={maxY}
                filteredTrack={filteredTrack}
                lineColor={lineColor}
              />
            )}
            <HeightChartPolyline
              filteredTrack={filteredTrack}
              maxX={maxX}
              minY={minY}
              maxY={maxY}
              bounds={bounds}
              lineColor={lineColor}
            />
            {elevationShowProgress && leadingStarterInfo && (
              <HeightChartProgressPolyline
                filteredTrack={filteredTrack}
                maxX={maxX}
                minY={minY}
                maxY={maxY}
                bounds={bounds}
                progress={leadingStarterInfo.offset}
                lineColor={lineColor}
              />
            )}
            <HeightChartStarterDots
              renderInfoBuffer={renderInfoBuffer}
              renderInfoOffset={renderInfoBuffer.renderIndexOffsets.get(currentEvent1.index) || 0}
              renderInfoCount={currentEvent1.starters.length}
              bounds={bounds}
              maxX={maxX}
              minY={minY}
              maxY={maxY}
              filteredTrack={filteredTrack}
              lineColor={lineColor}
            />
          </>
        );
      }}
    </SVGChart>
  );
}
